import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, ApiError, type User } from '../lib/api';

export default function UserManagement() {
  const queryClient = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
  });

  const inviteMutation = useMutation({
    mutationFn: () => usersApi.invite(inviteEmail, inviteRole),
    onSuccess: () => {
      setSuccess('招待メールを送信しました');
      setError('');
      setInviteEmail('');
      setInviteRole('user');
      setShowInviteModal(false);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('招待に失敗しました');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    inviteMutation.mutate();
  };

  const handleDelete = (user: User) => {
    if (confirm(`${user.name}を無効化しますか？`)) {
      deleteMutation.mutate(user.id);
    }
  };

  const getRoleBadge = (role: string) => {
    const styles: Record<string, string> = {
      admin: 'bg-purple-500 bg-opacity-20 text-purple-500',
      user: 'bg-blue-500 bg-opacity-20 text-blue-500',
      viewer: 'bg-gray-500 bg-opacity-20 text-gray-500',
    };
    const labels: Record<string, string> = {
      admin: '管理者',
      user: 'ユーザー',
      viewer: '閲覧者',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs ${styles[role] || styles.user}`}>
        {labels[role] || role}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-500 bg-opacity-20 text-green-500',
      pending: 'bg-yellow-500 bg-opacity-20 text-yellow-500',
      disabled: 'bg-red-500 bg-opacity-20 text-red-500',
    };
    const labels: Record<string, string> = {
      active: 'アクティブ',
      pending: '招待中',
      disabled: '無効',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-theme-primary">ユーザー管理</h1>
        <button
          onClick={() => setShowInviteModal(true)}
          className="px-4 py-2 bg-accent text-navy-900 font-bold rounded-lg hover:opacity-90 transition-opacity"
        >
          + ユーザーを招待
        </button>
      </div>

      {success && (
        <div className="p-3 bg-green-500 bg-opacity-20 border border-green-500 rounded-lg text-green-500 text-sm">
          {success}
        </div>
      )}

      <div className="bg-theme-card rounded-xl border border-theme overflow-hidden transition-colors duration-300">
        {isLoading ? (
          <div className="p-8 text-center text-theme-secondary">読み込み中...</div>
        ) : users && users.length > 0 ? (
          <table className="w-full">
            <thead className="bg-theme-hover">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary">ユーザー</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary">役割</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary">ステータス</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary">最終ログイン</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-theme-secondary"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-theme-hover transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent text-navy-900 flex items-center justify-center font-bold">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="text-theme-primary font-medium">
                          {user.name}
                          {user.isMaster && <span className="ml-2 text-accent text-xs">👑 マスター</span>}
                        </p>
                        <p className="text-sm text-theme-secondary">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">{getRoleBadge(user.role)}</td>
                  <td className="px-4 py-4">{getStatusBadge(user.status || 'pending')}</td>
                  <td className="px-4 py-4 text-theme-secondary">
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString('ja-JP') : '-'}
                  </td>
                  <td className="px-4 py-4">
                    {!user.isMaster && (
                      <button
                        onClick={() => handleDelete(user)}
                        className="text-red-500 hover:text-red-400 text-sm"
                      >
                        無効化
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-theme-secondary">
            ユーザーがいません
          </div>
        )}
      </div>

      {/* 招待モーダル */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-theme-card rounded-xl p-6 w-full max-w-md border border-theme transition-colors duration-300">
            <h2 className="text-xl font-bold text-theme-primary mb-4">ユーザーを招待</h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg text-red-500 text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  メールアドレス
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="user@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  役割
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="user">ユーザー</option>
                  <option value="admin">管理者</option>
                  <option value="viewer">閲覧者</option>
                </select>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 py-3 border border-theme text-theme-secondary rounded-lg hover:bg-theme-hover transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={inviteMutation.isPending}
                  className="flex-1 py-3 bg-accent text-navy-900 font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {inviteMutation.isPending ? '送信中...' : '招待を送信'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
